import { getStateFromPath, PathConfigMap } from '@react-navigation/native';
import qs, { ParsedQs } from 'qs';
import type { AppModalParams } from '../../flows/Routes';

type GetStateFromPath = typeof getStateFromPath;

// https://reactnavigation.org/docs/navigation-state
export type PathResolveResult = ReturnType<GetStateFromPath>;
export type PathResolveOptions = {
  initialRouteName?: string;
  screens: PathConfigMap<AppModalParams>;
};

export type PathResolverContext = {
  readonly originalPath: string;
  readonly url: URL;
  readonly params: URLSearchParams;
  readonly path: string;
  /** @property the path with leading slash stripped */
  readonly urlPath: string;
  readonly options: PathResolveOptions | undefined;
};

export interface PathResolver {
  resolveStateFromPath(): PathResolveResult;
}

export abstract class AbstractPathResolver implements PathResolver {
  constructor(protected readonly context: PathResolverContext) {}

  abstract resolveStateFromPath(): PathResolveResult;

  /**
   * Transform params from (serialized by `qs` in `getPathFromState`)
   *
   *    { "a[b]": 1, "a[c]": 2 }
   *
   * to
   *
   *    { a: { b: 1, c: 2 } }
   */
  protected parseQs(state: PathResolveResult): PathResolveResult {
    if (state == null) return state;
    return {
      ...state,
      routes: state.routes.map(r => ({
        ...r,
        state: this.parseQs(r.state),
        params: transformParams(r.params),
      })),
    };
    function transformParams(
      params: NonNullable<PathResolveResult>['routes'][number]['params'],
    ): undefined | ParsedQs {
      if (params == null) return params;
      return qs.parse(qs.stringify(params));
    }
  }

  protected getDefaultState(options?: PathResolveOptions) {
    return this.getStateFromPath(
      this.context.originalPath,
      options ?? this.context.options,
    );
  }

  protected getStateFromPath(path: string, options?: PathResolveOptions) {
    return this.parseQs(
      getStateFromPath(path, options ?? this.context.options),
    );
  }
}
